<template>
  <AppLayout>
    <div class="logistique-page">
      <div class="page-header">
        <h1>Logistique</h1>
        <BaseButton variant="ghost" @click="loadExpeditions">Rafraîchir</BaseButton>
      </div>

      <BaseCard class="filters-card">
        <div class="filters-grid">
          <BaseSelect v-model="selectedProjetId" label="Projet">
            <option value="">Tous les projets</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>

          <BaseSelect v-model="selectedSiteId" label="Site">
            <option value="">Tous les sites</option>
            <option v-for="site in filteredSites" :key="site.id" :value="String(site.id)">
              {{ site.nom }}
            </option>
          </BaseSelect>

          <BaseSelect v-model="selectedStatut" label="Statut">
            <option value="">Tous</option>
            <option value="PLANIFIE">Planifié</option>
            <option value="EN_TRANSIT">En transit</option>
            <option value="LIVRE">Livré</option>
            <option value="PARTIEL">Partiel</option>
            <option value="ANNULE">Annulé</option>
          </BaseSelect>

          <BaseInput
            v-model="selectedDate"
            type="date"
            label="Date expédition"
          />
        </div>
      </BaseCard>

      <div v-if="error" class="error">{{ error }}</div>

      <div class="kpis-grid">
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.aValider }}</div>
          <div class="kpi-label">A valider</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.enRoute }}</div>
          <div class="kpi-label">En route</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.livreAujourdhui }}</div>
          <div class="kpi-label">Livré aujourd'hui</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.litiges }}</div>
          <div class="kpi-label">Litiges</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.piecesEnRoute }}</div>
          <div class="kpi-label">Pièces en route</div>
        </BaseCard>
      </div>

      <section class="panel-section">
        <div class="section-header">
          <h2>Livraisons</h2>
        </div>
        <BaseTable
          :columns="columns"
          :items="expeditionsFiltered"
          :loading="loading"
          :show-actions="true"
          @view="viewExpedition"
        >
          <template #cell-dateExpedition="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-projet="{ item }">
            {{ item.projetNom }} ({{ item.projetCode }})
          </template>
          <template #cell-totalPieces="{ item }">
            {{ itemTotals[item.id] ?? 0 }}
          </template>
          <template #cell-statut="{ value }">
            <BaseBadge :variant="getStatutVariant(value)">{{ value }}</BaseBadge>
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="viewExpedition(item)">👁️ Voir</BaseButton>
            <BaseButton
              v-if="item.statut === 'PLANIFIE'"
              variant="secondary"
              size="sm"
              :loading="statusLoadingId === item.id"
              @click="updateStatus(item.id, 'EN_TRANSIT')"
            >
              Accepter
            </BaseButton>
            <BaseButton
              v-if="item.statut === 'EN_TRANSIT'"
              variant="primary"
              size="sm"
              :loading="statusLoadingId === item.id"
              @click="updateStatus(item.id, 'LIVRE')"
            >
              Marquer livrée
            </BaseButton>
          </template>
        </BaseTable>
      </section>
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseBadge from '../components/BaseBadge.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseCard from '../components/BaseCard.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseTable, { type TableColumn } from '../components/BaseTable.vue';
import { apiFetch } from '../utils/api';

interface Projet {
  id: number;
  nom: string;
  code: string;
}

interface Site {
  id: number;
  nom: string;
  projetId: number;
}

interface Expedition {
  id: number;
  projetId: number;
  projetNom?: string;
  projetCode?: string;
  siteId: number;
  siteNom?: string;
  dateExpedition: string;
  idVehicule: string | null;
  nomChauffeur: string | null;
  statut: 'PLANIFIE' | 'EN_TRANSIT' | 'LIVRE' | 'PARTIEL' | 'ANNULE';
}

interface ExpeditionItem {
  id: number;
  typeElementPrefabriqueId: number;
  quantiteEnvoyee: number;
}

const router = useRouter();

const projets = ref<Projet[]>([]);
const sites = ref<Site[]>([]);
const expeditions = ref<Expedition[]>([]);
const itemTotals = ref<Record<number, number>>({});

const selectedProjetId = ref('');
const selectedSiteId = ref('');
const selectedStatut = ref('');
const selectedDate = ref('');

const loading = ref(false);
const error = ref<string | null>(null);
const statusLoadingId = ref<number | null>(null);

const columns: TableColumn[] = [
  { key: 'dateExpedition', label: 'Date' },
  { key: 'projet', label: 'Projet' },
  { key: 'siteNom', label: 'Site' },
  { key: 'nomChauffeur', label: 'Chauffeur' },
  { key: 'idVehicule', label: 'Véhicule' },
  { key: 'totalPieces', label: 'Pièces' },
  { key: 'statut', label: 'Statut' }
];

const filteredSites = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const scoped = projetId === null ? sites.value : sites.value.filter((site) => site.projetId === projetId);
  return [...scoped].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
});

const expeditionsFiltered = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);

  return expeditions.value.filter((expedition) => {
    if (projetId !== null && expedition.projetId !== projetId) {
      return false;
    }
    if (siteId !== null && expedition.siteId !== siteId) {
      return false;
    }
    if (selectedStatut.value && expedition.statut !== selectedStatut.value) {
      return false;
    }
    if (selectedDate.value && expedition.dateExpedition !== selectedDate.value) {
      return false;
    }
    return true;
  });
});

const kpis = computed(() => {
  const today = toDateInput(new Date());
  return expeditionsFiltered.value.reduce(
    (acc, expedition) => {
      const qty = itemTotals.value[expedition.id] ?? 0;
      if (expedition.statut === 'PLANIFIE') {
        acc.aValider += 1;
      }
      if (expedition.statut === 'EN_TRANSIT') {
        acc.enRoute += 1;
        acc.piecesEnRoute += qty;
      }
      if ((expedition.statut === 'LIVRE' || expedition.statut === 'PARTIEL') && expedition.dateExpedition === today) {
        acc.livreAujourdhui += 1;
      }
      if (expedition.statut === 'PARTIEL') {
        acc.litiges += 1;
      }
      return acc;
    },
    {
      aValider: 0,
      enRoute: 0,
      livreAujourdhui: 0,
      litiges: 0,
      piecesEnRoute: 0
    }
  );
});

watch(
  () => selectedProjetId.value,
  () => {
    const projetId = toNumberOrNull(selectedProjetId.value);
    const siteId = toNumberOrNull(selectedSiteId.value);
    if (siteId !== null && !sites.value.some((site) => site.id === siteId && site.projetId === projetId)) {
      selectedSiteId.value = '';
    }
  }
);

watch(
  () => expeditionsFiltered.value.map((expedition) => expedition.id).join(','),
  () => {
    ensureItemTotals(expeditionsFiltered.value);
  }
);

onMounted(async () => {
  await loadProjetsAndSites();
  await loadExpeditions();
});

async function loadProjetsAndSites() {
  try {
    const response = await apiFetch('/projets');
    projets.value = (response as { items: Projet[] }).items || [];

    const siteRequests = projets.value.map(async (projet) => {
      const res = await apiFetch(`/projets/${projet.id}/sites`);
      const items = (res as { items: Array<{ id: number; nom: string }> }).items || [];
      return items.map((site) => ({
        id: site.id,
        nom: site.nom,
        projetId: projet.id
      })) satisfies Site[];
    });

    sites.value = (await Promise.all(siteRequests)).flat();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des projets/sites';
  }
}

async function loadExpeditions() {
  loading.value = true;
  error.value = null;
  try {
    const response = await apiFetch('/expeditions?limit=500&offset=0');
    expeditions.value = (response as { items: Expedition[] }).items || [];
    await ensureItemTotals(expeditions.value);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des expéditions';
  } finally {
    loading.value = false;
  }
}

async function ensureItemTotals(list: Expedition[]) {
  const missing = list.filter((expedition) => itemTotals.value[expedition.id] === undefined);
  if (missing.length === 0) {
    return;
  }

  const entries = await Promise.all(
    missing.map(async (expedition) => {
      try {
        const response = await apiFetch(`/expeditions/${expedition.id}/items`);
        const items = (response as { items: ExpeditionItem[] }).items || [];
        const total = items.reduce((sum, item) => sum + item.quantiteEnvoyee, 0);
        return [expedition.id, total] as const;
      } catch {
        return [expedition.id, 0] as const;
      }
    })
  );

  const nextMap = { ...itemTotals.value };
  entries.forEach(([id, total]) => {
    nextMap[id] = total;
  });
  itemTotals.value = nextMap;
}

async function updateStatus(expeditionId: number, statut: Expedition['statut']) {
  statusLoadingId.value = expeditionId;
  error.value = null;
  try {
    await apiFetch(`/expeditions/${expeditionId}`, {
      method: 'PATCH',
      body: { statut }
    });
    await loadExpeditions();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors de la mise à jour du statut';
  } finally {
    statusLoadingId.value = null;
  }
}

function viewExpedition(expedition: Expedition) {
  router.push({ name: 'expedition-detail', params: { id: expedition.id } });
}

function getStatutVariant(statut: Expedition['statut']): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (statut) {
    case 'PLANIFIE':
      return 'default';
    case 'EN_TRANSIT':
      return 'primary';
    case 'LIVRE':
      return 'success';
    case 'PARTIEL':
      return 'warning';
    case 'ANNULE':
      return 'error';
    default:
      return 'default';
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('fr-FR');
}

function toDateInput(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toNumberOrNull(value: string): number | null {
  if (!value.trim()) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
</script>

<style scoped>
.logistique-page {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-header h1 {
  margin: 0;
}

.filters-card {
  padding: var(--spacing-md);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-md);
}

.kpis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: var(--spacing-md);
}

.kpi-card {
  padding: var(--spacing-lg);
  text-align: center;
}

.kpi-value {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.kpi-label {
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.panel-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-header h2 {
  margin: 0;
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
}
</style>
